
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import essService from "../api/services/essService";
import fileUploadService from "../api/services/fileUploadService";
import { toast } from "../utils/toast";

export const useProfile = () => {
    return useQuery({
        queryKey: ["profile"],
        queryFn: async () => {
            const response = await essService.getProfile();
            if (!response.success && !response.data) {
                throw new Error(response.error || "Failed to fetch profile");
            }
            return response.data;
        },
        // Don't refetch on window focus automatically to prevent overwriting form edits
        refetchOnWindowFocus: false,
        staleTime: 0,
        retry: 1,
    });
};

export const useComplianceDetails = () => {
    return useQuery({
        queryKey: ["compliance"],
        queryFn: async () => {
            const response = await essService.getComplianceDetails();
            if (!response.success && !response.data) {
                throw new Error(response.error || "Failed to fetch compliance details");
            }
            return response.data;
        },
        enabled: false, // Don't fetch automatically, only when requested
    });
};

export const useDocuments = () => {
    return useQuery({
        queryKey: ["documents"],
        queryFn: async () => {
            const response = await essService.getDocuments();
            if (!response.success) {
                throw new Error(response.error || "Failed to fetch documents");
            }
            return response.data || [];
        },
    });
};

export const useDocumentTypes = () => {
    return useQuery({
        queryKey: ["documentTypes"],
        queryFn: async () => {
            try {
                const response = await essService.getDocumentTypes();
                if (!response.success) {
                    return [];
                }
                return response.data || [];
            } catch (error) {
                return [];
            }
        },
        refetchOnWindowFocus: false,
        staleTime: 300000, // 5 minutes
    });
};

export const useUpdateProfile = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data) => {
            const response = await essService.updateProfile(data);
            if (!response.success) {
                throw new Error(response.error || "Failed to update profile");
            }
            return response.data;
        },
        onSuccess: () => {
            toast.success("Profile updated successfully!");
            queryClient.invalidateQueries(["profile"]);
            queryClient.invalidateQueries(["documents"]);
        },
        onError: (error) => {
            toast.error(error.message || "Failed to update profile");
        },
    });
};

export const useUploadDocument = () => {
    return useMutation({
        mutationFn: async ({ file, onProgress }) => {
            const response = await essService.uploadDocument(file, onProgress);
            if (!response.success) {
                throw new Error(response.error || "Failed to upload document");
            }
            return response.data;
        },
        onError: (error) => {
            toast.error(error.message || "Failed to upload document");
        },
    });
};

export const useDeleteTempDocument = () => {
    return useMutation({
        mutationFn: async ({ publicId, resourceType }) => {
            const response = await essService.deleteTempDocument(publicId, resourceType);
            if (!response.success) {
                throw new Error(response.error || "Failed to delete document");
            }
            return response;
        },
        onError: (error) => {
        },
    });
};

export const useUploadProfilePicture = () => {
    return useMutation({
        mutationFn: async (file) => {
            const response = await fileUploadService.uploadProfilePicture(file);
            if (!response.success) {
                throw new Error(response.error || "Failed to upload profile picture");
            }
            return response;
        },
        onError: (error) => {
            toast.error(error.message || "Failed to upload profile picture");
        },
    });
};
